import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import Badge from '../bookings/Badge';
import UsageBar from '../usage/UsageBar';
import type { UsageListResponse, UsageRow } from '../usage/types';
import {
  consumed_pct,
  days_remaining,
  fmt_pct,
  messages_pct,
  minutes_pct,
  sort_by_consumed,
  status_color,
  status_label,
  status_of,
  type SortDir,
} from '../usage/usage-logic';

function MetricCell({ used, limit, percent }: { used: number; limit: number; percent: number }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 130 }}>
      <span style={{ fontSize: 13 }}>
        <strong>{Math.round(used)}</strong> / {limit} ({fmt_pct(percent)})
      </span>
      <UsageBar percent={percent} color={status_color(status_of_from_pct(percent))} />
    </div>
  );
}

// A per-metric bar colors off that metric's own percent (not the row's headline status),
// so an operator sees which resource is hot at a glance.
function status_of_from_pct(percent: number): 'ok' | 'warn' | 'over' {
  if (percent >= 100) return 'over';
  if (percent >= 80) return 'warn';
  return 'ok';
}

/** Cross-tenant usage table (Prompt A.2). One row per tenant with a captomi_office app,
 *  sortable by % consumed (default highest-first), row click → tenant detail. */
export default function Usage() {
  const navigate = useNavigate();
  const [dir, set_dir] = useState<SortDir>('desc');

  const { data, isLoading, error } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get<UsageListResponse>('/api/captomi-office/usage'),
  });

  const rows = useMemo(
    () => sort_by_consumed(data?.usage ?? [], dir),
    [data, dir],
  );

  const columns = [
    {
      header: 'Tenant',
      cell: (r: UsageRow) => <strong>{r.business_name ?? r.tenant_id}</strong>,
    },
    {
      header: 'Plan',
      cell: (r: UsageRow) => (
        <span style={{ fontSize: 13 }}>
          <span style={{ textTransform: 'capitalize' }}>{r.plan_tier}</span>
          {typeof r.monthly_price_usd === 'number' && (
            <span className="muted"> · ${r.monthly_price_usd}</span>
          )}
        </span>
      ),
    },
    {
      header: 'Minutes',
      cell: (r: UsageRow) => (
        <MetricCell used={r.used.minutes} limit={r.limits.minutes} percent={minutes_pct(r)} />
      ),
    },
    {
      header: 'Messages',
      cell: (r: UsageRow) => (
        <MetricCell used={r.used.messages} limit={r.limits.messages} percent={messages_pct(r)} />
      ),
    },
    {
      header: 'Days left',
      cell: (r: UsageRow) => days_remaining(r.cycle.end),
      width: '90px',
    },
    {
      header: 'Status',
      cell: (r: UsageRow) => {
        const s = status_of(r);
        return <Badge color={status_color(s)}>{status_label(s)}</Badge>;
      },
      width: '90px',
    },
    {
      header: 'Consumed',
      cell: (r: UsageRow) => <strong>{fmt_pct(consumed_pct(r))}</strong>,
      width: '110px',
    },
  ];

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <h1 style={{ margin: 0 }}>Usage</h1>
        <button
          className="secondary"
          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
          onClick={() => set_dir((d) => (d === 'desc' ? 'asc' : 'desc'))}
        >
          Consumed: {dir === 'desc' ? 'highest first ↓' : 'lowest first ↑'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
        captomi-office minute + message usage, one row per tenant with the app.
      </p>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading usage…</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <DataTable
            rows={rows}
            columns={columns}
            on_row_click={(r) => navigate(`/tenants/${r.tenant_id}`)}
            empty_message="No tenants have a captomi_office app yet."
          />
        )}
      </div>
    </div>
  );
}
